import { Link } from 'react-router-dom';
import { Fish, Mail, MapPin, Phone, Facebook, Instagram, Settings, LogOut, Moon, Sun, History } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { ChangelogModal } from './ChangelogModal';
import axios from 'axios';

import { API } from '../config/api';

export const Footer = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showChangelog, setShowChangelog] = useState(false);
  const { isAuthenticated, user, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email });
      toast.success('Inscription réussie ! Bienvenue dans notre communauté.');
      setEmail('');
    } catch (error) {
      if (error.response?.status === 400) {
        toast.info('Vous êtes déjà inscrit à notre newsletter.');
      } else {
        toast.error('Une erreur est survenue. Veuillez réessayer.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer data-testid="footer" className="bg-[#0F172A] text-white">
      {/* Newsletter Section */}
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-16">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="text-center md:text-left">
              <h3 className="font-['Playfair_Display'] text-2xl md:text-3xl font-semibold mb-2">
                Rejoignez notre Newsletter
              </h3>
              <p className="text-white/70">
                Recevez nos guides, actualités et conseils sur les Nishikigoi.
              </p>
            </div>
            <form onSubmit={handleNewsletterSubmit} className="flex w-full md:w-auto gap-3">
              <Input
                type="email"
                placeholder="Votre email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="newsletter-email-input"
                className="bg-white/10 border-white/20 text-white placeholder:text-white/50 w-full md:w-72"
                required
              />
              <Button
                type="submit"
                data-testid="newsletter-submit-btn"
                disabled={isSubmitting}
                className="bg-[#D32F2F] hover:bg-[#D32F2F]/90 text-white rounded-lg shrink-0"
              >
                {isSubmitting ? 'Envoi...' : "S'inscrire"}
              </Button>
            </form>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 md:gap-8">
          {/* Brand */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-3 mb-4">
              <Fish className="w-8 h-8 text-[#D32F2F]" />
              <span className="font-['Playfair_Display'] text-xl font-semibold">
                Nishikigoi.fr
              </span>
            </Link>
            <p className="text-white/70 text-sm leading-relaxed mb-6">
              Découvrez l'art vivant des carpes koï japonaises. Guide complet pour collectionneurs, éleveurs et passionnés.
            </p>
            <div className="flex gap-4">
              <a
                href="https://www.facebook.com/afloredeau.fr"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-facebook"
                aria-label="Visitez notre page Facebook"
                className="w-11 h-11 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href="https://www.instagram.com/afloredeau/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-instagram"
                aria-label="Visitez notre page Instagram"
                className="w-11 h-11 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href="https://www.tiktok.com/@afloredeau"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-tiktok"
                aria-label="Visitez notre page TikTok"
                className="w-11 h-11 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Navigation */}
          <div>
            <h4 className="font-semibold text-lg mb-4">Navigation</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/histoire" className="text-white/70 hover:text-white transition-colors">
                  Histoire & Culture
                </Link>
              </li>
              <li>
                <Link to="/varietes" className="text-white/70 hover:text-white transition-colors">
                  Variétés de Koï
                </Link>
              </li>
              <li>
                <Link to="/eleveurs" className="text-white/70 hover:text-white transition-colors">
                  Éleveurs Japonais
                </Link>
              </li>
              <li>
                <Link to="/concours" className="text-white/70 hover:text-white transition-colors">
                  Concours & Événements
                </Link>
              </li>
              <li>
                <Link to="/blog" className="text-white/70 hover:text-white transition-colors">
                  Blog
                </Link>
              </li>
            </ul>
          </div>

          {/* Partners */}
          <div>
            <h4 className="font-semibold text-lg mb-4">Nos Partenaires</h4>
            <ul className="space-y-3">
              <li>
                <a
                  href="https://www.koi-shop.fr"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white/70 hover:text-white transition-colors"
                >
                  Koi-Shop.fr
                </a>
              </li>
              <li>
                <a
                  href="https://afloredeau.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white/70 hover:text-white transition-colors"
                >
                  A Flore d'Eau
                </a>
              </li>
              <li>
                <a
                  href="https://tamasaba.fr"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white/70 hover:text-white transition-colors"
                >
                  Tamasaba.fr
                </a>
              </li>
              <li>
                <a
                  href="https://medaka.fr"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white/70 hover:text-white transition-colors"
                >
                  Medaka.fr
                </a>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-semibold text-lg mb-4">Contact</h4>
            <ul className="space-y-3">
              <li className="flex items-center gap-3 text-white/70">
                <Mail className="w-5 h-5 text-[#D32F2F]" />
                <a href="mailto:afloredeau@gmail.com" className="hover:text-white transition-colors">
                  afloredeau@gmail.com
                </a>
              </li>
              <li className="flex items-center gap-3 text-white/70">
                <Phone className="w-5 h-5 text-[#D32F2F]" />
                <span>06 50 14 13 46</span>
              </li>
              <li className="flex items-start gap-3 text-white/70">
                <MapPin className="w-5 h-5 text-[#D32F2F] shrink-0" />
                <span>France</span>
              </li>
            </ul>
            <Link to="/contact" className="inline-block mt-4">
              <Button className="bg-[#D32F2F] hover:bg-[#B71C1C] text-white rounded-lg">
                Nous contacter
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-white/60">
            <p>© 2026 Nishikigoi.fr – Tous droits réservés. <button 
                onClick={() => setShowChangelog(true)}
                className="text-white/40 hover:text-white/70 ml-2 inline-flex items-center gap-1 transition-colors cursor-pointer"
                title="Voir l'historique des versions"
              >
                <History className="w-3 h-3" />
                V1.5.0
              </button></p>
            <div className="flex items-center gap-6">
              {/* Theme Toggle */}
              <button
                onClick={toggleTheme}
                data-testid="theme-toggle"
                className="p-2 rounded-full hover:bg-white/10 transition-colors"
                title={isDark ? "Mode clair" : "Mode sombre"}
              >
                {isDark ? (
                  <Sun className="w-5 h-5 text-yellow-400" />
                ) : (
                  <Moon className="w-5 h-5 text-white/60 hover:text-white" />
                )}
              </button>
              
              <Link to="/mentions-legales" className="hover:text-white transition-colors">
                Mentions légales
              </Link>
              <Link to="/mentions-legales" className="hover:text-white transition-colors">
                Confidentialité
              </Link>
              {/* Admin Login/Logout Button */}
              {isAuthenticated ? (
                <div className="flex items-center gap-3 pl-4 border-l border-white/20">
                  <Link to="/admin">
                    <Button
                      size="sm"
                      data-testid="footer-admin-btn"
                      className="bg-[#D32F2F] hover:bg-[#B71C1C] text-white rounded-full"
                    >
                      <Settings className="w-4 h-4 mr-1" />
                      Admin ({user?.username})
                    </Button>
                  </Link>
                  <button
                    onClick={logout}
                    className="text-white/60 hover:text-white transition-colors flex items-center gap-1"
                    data-testid="footer-logout-btn"
                  >
                    <LogOut className="w-4 h-4" />
                    Déconnexion
                  </button>
                </div>
              ) : (
                <Link to="/login" className="pl-6 border-l border-white/20">
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid="footer-login-btn"
                    className="border-white/30 text-white hover:bg-white/10 rounded-full min-h-[44px] px-4"
                  >
                    <Settings className="w-4 h-4 mr-1" />
                    Administration
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Changelog Modal */}
      <ChangelogModal isOpen={showChangelog} onClose={() => setShowChangelog(false)} />
    </footer>
  );
};

export default Footer;
